import React, { useEffect, useState } from 'react';
import { Button } from '@mui/material';
import BaseModal from '../../../utils/BaseModal';
import api from '../../../../api/axios';
import type { KycStatus } from '../../../../../types/user/user';

type Props = {
  open: boolean;
  userId?: number;
  onClose: () => void;
  onUpdateKycStatus: (status: string) => void;
};

const UserKycEditModal = ({ open, userId, onClose, onUpdateKycStatus }: Props) => {
  const [loading, setLoading] = useState(false);
  const [kycStatus, setKycStatus] = useState<KycStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newStatus, setNewStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !userId) return;

    setLoading(true);
    setError(null);

    api.get(`/user/${userId}/kyc-status`)
    .then(res => {
      setKycStatus(res.data);
      setNewStatus(res.data.status);
    })
    .catch(() => setError('Failed to fetch KYC status'))
    .finally(() => setLoading(false));
  }, [open, userId]);

  const handleStatusChange = (status: string) => setNewStatus(status);

  const handleSave = async () => {
    if (!newStatus || !userId) return;

    setLoading(true);
    setError(null);

    try {
      await api.post(`/user/${userId}/kyc-status`, null, {
        params: { status: newStatus }
      });

      setKycStatus(prev => prev ? { ...prev, status: newStatus } : null);
      onUpdateKycStatus(newStatus);
      onClose();
    } catch {
      setError('Failed to update KYC status');
    } finally {
      setLoading(false);
    }
  };

  const renderStatusBadge = (status?: string) => {
    switch (status) {
      case 'APPROVED':
        return <span className="text-green-500 font-semibold">Verified</span>;
      case 'PENDING':
      case 'REVIEW':
        return <span className="text-yellow-500 font-semibold">In review</span>;
      case 'DECLINED':
        return <span className="text-red-500 font-semibold">Declined</span>;
      default:
        return <span className="text-gray-400 font-semibold">Not verified</span>;
    }
  };

  const rejectionReasonList = kycStatus?.rejectionReason?.length
      ? (
          <div className="text-red-500 mt-2">
            {kycStatus.rejectionReason.map((errMap, idx) => (
                <div key={idx}>{errMap['shortDescription'] || 'Unknown reason'}</div>
            ))}
          </div>
      )
      : null;

  let modalContent;
  if (loading) modalContent = <div>Loading...</div>;
  else if (error) modalContent = <div className="text-red-500">{error}</div>;
  else modalContent = (
        <div className="flex flex-col gap-3">
          <div>
            Current status: {renderStatusBadge(kycStatus?.status)}
          </div>

          {/* First row: 4 Buttons */}
          <div className="flex gap-2 flex-wrap">
            <Button variant="contained"
                    onClick={() => handleStatusChange('NOT_VERIFIED')}
                    sx={{
                      backgroundColor: newStatus === 'NOT_VERIFIED' ? '#2563EB' : '#374151', // blue-600 / gray-700
                      color: newStatus === 'NOT_VERIFIED' ? '#FFFFFF' : '#E5E7EB', // white / gray-200
                      '&:hover': { backgroundColor: newStatus === 'NOT_VERIFIED' ? '#1D4ED8' : '#4B5563' } // darker on hover
                    }}>
              Not Verified
            </Button>

            <Button variant="contained"
                    onClick={() => handleStatusChange('NOT_STARTED')}
                    sx={{
                      backgroundColor: newStatus === 'NOT_STARTED' ? '#2563EB' : '#374151',
                      color: newStatus === 'NOT_STARTED' ? '#FFFFFF' : '#E5E7EB',
                      '&:hover': { backgroundColor: newStatus === 'NOT_STARTED' ? '#1D4ED8' : '#4B5563' }
                    }}>
              Not Started
            </Button>

            <Button variant="contained"
                    onClick={() => handleStatusChange('APPROVED')}
                    sx={{
                      backgroundColor: newStatus === 'APPROVED' ? '#22C55E' : '#374151', // green-500 / gray-700
                      color: newStatus === 'APPROVED' ? '#FFFFFF' : '#E5E7EB',
                      '&:hover': { backgroundColor: newStatus === 'APPROVED' ? '#16A34A' : '#4B5563' }
                    }}>
              Approved
            </Button>

            <Button variant="contained"
                    onClick={() => handleStatusChange('REVIEW')}
                    sx={{
                      backgroundColor: newStatus === 'REVIEW' ? '#EAB308' : '#374151', // yellow-500 / gray-700
                      color: newStatus === 'REVIEW' ? '#1F2937' : '#E5E7EB', // dark text on yellow
                      '&:hover': { backgroundColor: newStatus === 'REVIEW' ? '#CA8A04' : '#4B5563' }
                    }}>
              Review
            </Button>
          </div>

          {/* Second row: 3 Buttons */}
          <div className="flex gap-2 flex-wrap mt-2">
            <Button variant="contained"
                    onClick={() => handleStatusChange('RENEW')}
                    sx={{
                      backgroundColor: newStatus === 'RENEW' ? '#EAB308' : '#374151',
                      color: newStatus === 'RENEW' ? '#1F2937' : '#E5E7EB',
                      '&:hover': { backgroundColor: newStatus === 'RENEW' ? '#CA8A04' : '#4B5563' }
                    }}>
              Renew
            </Button>

            <Button variant="contained"
                    onClick={() => handleStatusChange('PENDING')}
                    sx={{
                      backgroundColor: newStatus === 'PENDING' ? '#EAB308' : '#374151',
                      color: newStatus === 'PENDING' ? '#1F2937' : '#E5E7EB',
                      '&:hover': { backgroundColor: newStatus === 'PENDING' ? '#CA8A04' : '#4B5563' }
                    }}>
              Pending
            </Button>

            <Button variant="contained"
                    onClick={() => handleStatusChange('DECLINED')}
                    sx={{
                      backgroundColor: newStatus === 'DECLINED' ? '#EF4444' : '#374151', // red-500 / gray-700
                      color: newStatus === 'DECLINED' ? '#FFFFFF' : '#E5E7EB',
                      '&:hover': { backgroundColor: newStatus === 'DECLINED' ? '#DC2626' : '#4B5563' }
                    }}>
              Declined
            </Button>
          </div>

          {rejectionReasonList}
        </div>
    );

  return (
      <BaseModal
          open={open}
          title="Edit KYC Status"
          onAbort={onClose}
          onSubmit={handleSave}
          abortText="Cancel"
          submitText="Save"
      >
        {modalContent}
      </BaseModal>
  );
};

export default UserKycEditModal;
